import React, { useContext, useState } from "react";

import * as jose from "jose";

import {
  Box,
  Button,
  Caption,
  DetailedSubheading,
  Divider,
  Flex,
  Textarea,
  Toggle,
} from "@olist/design-system";
import { SuccessOutline } from "@olist/design-system-icons";
import { useApplication } from "@olist/microfrontend";

import {
  FileUpload,
  FormHeader,
  Input,
  ReasonFormStep,
  SystemFormStep,
} from "./components";
import { UploadTitle } from "./components/FileUpload/styles";
import { FinanceFormStep } from "./components/FinanceFormStep";
import { SuccessStep } from "./components/SuccessStep";
import {
  getFieldsByReason,
  getSubReasonOptions,
} from "./components/SystemFormStep/constants";
import { IERPProps } from "./types";
import { useToast } from "components/toast";
import { MixpanelContext } from "contexts/mixpanel";
import { useGetSupportUserStatus, useNewTicket } from "pages/new-ticket/hooks";
import { queryClient } from "utils/react-query";

export function ERP({ email }: IERPProps) {
  const { trackAgent } = useContext(MixpanelContext);
  const { data: supportERP } = useGetSupportUserStatus();

  const [isSupportEnabled, setIsSupportEnabled] = useState(
    supportERP?.ativo || true
  );
  const [step, setStep] = useState<"reason" | "systemForm" | "financialForm">(
    "reason"
  );
  const [formData, setFormData] = useState<{ [key: string]: string }>({
    ...(email ? { SuppliedEmail: email as string } : { SuppliedEmail: "" }),
    supportUser: "true",
  });

  const [description, setDescription] = useState<string | undefined>(undefined);
  const [fieldsError, setFieldsError] = useState<string[]>([]);

  const context = useApplication();

  const { data, isPending, isSuccess, mutate } = useNewTicket();

  const { handleOpen } = useToast();

  const handleFormDataChange = (value: Record<string, string>) => {
    const newFormData = { ...formData, ...value };
    setFormData({
      ...newFormData,
    });

    const keys = Object.keys(value).filter(
      (key) =>
        ![
          "Assunto",
          "Motivo",
          "Submotivo",
          "Descrição",
          "Email de contato",
        ].includes(key)
    );
    if (
      keys.length > 0 &&
      keys.every((key) => Boolean(newFormData[key]) === true)
    ) {
      const newErrors = fieldsError.filter(
        (e) => e !== "Campos complementares"
      );
      setFieldsError(newErrors);
    }
  };

  const cleanErrors = (value: string) => {
    const newErrors = fieldsError.filter((e) => e !== value);
    setFieldsError(newErrors);
  };

  const handleValidation = () => {
    const error: string[] = [];
    if (
      !formData.Sobre_o_que_deseja_falar__c ||
      formData.Sobre_o_que_deseja_falar__c === "--None--"
    ) {
      error.push("Assunto");
    }
    if (
      !formData.Motivo_do_contato__c ||
      formData.Motivo_do_contato__c === "--None--"
    ) {
      error.push("Motivo");
    }

    if (
      getSubReasonOptions(formData.Motivo_do_contato__c).length > 0 &&
      (formData.Submotivo_do_contato__c === "" ||
        formData.Submotivo_do_contato__c === undefined ||
        formData.Submotivo_do_contato__c === "--None--")
    ) {
      error.push("Submotivo");
    }
    if (formData.Sobre_o_que_deseja_falar__c && formData.Motivo_do_contato__c) {
      const newFields = getFieldsByReason({
        subject: formData.Sobre_o_que_deseja_falar__c,
        reason: formData.Motivo_do_contato__c,
        subreason: formData.Submotivo_do_contato__c,
      }).filter(({ id }) => id !== "informativo");
      if (
        newFields.length > 0 &&
        newFields.some(
          ({ id }) =>
            formData[id as string] === "" ||
            formData[id as string] === undefined
        )
      ) {
        error.push("Campos complementares");
      }
    }
    if (!description) {
      error.push("Descrição");
    }

    if (!formData.SuppliedEmail) {
      error.push("Email de contato");
    }

    setFieldsError(error);
    return error.length === 0;
  };

  const handleSubmit = async () => {
    const isValid = handleValidation();
    if (isValid === false) {
      handleOpen({
        variation: "alert",
        message: `Os campos: ${fieldsError
          .join(", ")
          .replace(/,([^,]*)$/, " e$1")} não podem ser vazios`,
      });
      return;
    }

    const requesterId = await jose.decodeJwt(context.accessToken || "");
    const payload = {
      ...formData,
      Description: description,
      RecordTypeId: "012bL000001U3b0",
      requester_id__c: requesterId.sub,
      supportUser: formData.supportUser === "true",
      Origin: "Formulario ERP",
      Subject:
        `${formData.Sobre_o_que_deseja_falar__c} | ${
          formData.Motivo_do_contato__c
        } | ${formData.Submotivo_do_contato__c || ""}` || undefined,
    };

    mutate(
      { payload, param: "erp" },
      {
        onSuccess: async () => {
          trackAgent.track("helpcenter_submeteu_novoticket", payload);
          await queryClient.invalidateQueries();
        },
        onError: () => {
          trackAgent.track("helpcenter_submeteu_novoticket_erro", payload);
          handleOpen({
            message: "Encontramos um problema, tente novamente mais tarde",
            variation: "alert",
          });
        },
      }
    );
  };

  if (isSuccess) {
    return (
      <SuccessStep
        email={data?.email || formData.SuppliedEmail}
        ticketId={data?.salesforceCaseId}
      />
    );
  }

  return (
    <>
      <Box padding="40px" height="100%">
        <FormHeader />
        {step === "reason" && <ReasonFormStep setStep={setStep} />}
        {step === "financialForm" && (
          <FinanceFormStep
            formData={formData}
            handleFormDataChange={handleFormDataChange}
            fieldError={fieldsError}
            onCleanError={cleanErrors}
          />
        )}
        {step === "systemForm" && (
          <SystemFormStep
            formData={formData}
            handleFormDataChange={handleFormDataChange}
            fieldError={fieldsError}
            onCleanError={cleanErrors}
          />
        )}
      </Box>
      {step !== "reason" && Boolean(formData.Sobre_o_que_deseja_falar__c) && (
        <Flex
          flexDirection="column"
          marginBottom="24px"
          gap="stack.medium"
          paddingHorizontal="40px"
        >
          <Divider />
          <Flex flexDirection="column" gap="stack.small">
            <DetailedSubheading style={{ fontWeight: 500 }} bold>
              Descreva sua dúvida de forma detalhada
            </DetailedSubheading>
            <Textarea
              required
              name="Description"
              onChange={({ target }) => {
                setDescription(target.value);
                const newErrors = fieldsError.filter(
                  (value) => value !== "Descrição"
                );
                setFieldsError(newErrors);
              }}
              // label="Descrição do chamado"
              placeholder="Descreva sua dúvida"
              helperText={
                description &&
                (description.length < 15 || description.length > 4000)
                  ? "A descrição deve ter mais de 15 caracteres e menos de 4000 caracteres"
                  : undefined
              }
              feedback={
                (description &&
                  (description.length < 15 || description.length > 4000)) ||
                fieldsError.includes("Descrição")
                  ? "error"
                  : undefined
              }
            />
          </Flex>
          <Box>
            <UploadTitle>
              Adicione imagens para ajudar a explicar sua dúvida
            </UploadTitle>
            <FileUpload handleFormDataChange={handleFormDataChange} />
          </Box>
          <Flex gap="inline.small" alignItems="flex-start">
            <Toggle
              variation="success"
              checked={isSupportEnabled}
              onChange={() => {
                setIsSupportEnabled(!isSupportEnabled);
                handleFormDataChange({
                  supportUser: String(!isSupportEnabled),
                });
              }}
            />
            <Flex flexDirection="column">
              <Caption bold>Usuário de suporte</Caption>
              <Caption>
                Quando habilitado, o time de suporte tem acesso temporário a sua
                conta até 15 dias depois de ser habilitado
              </Caption>
            </Flex>
          </Flex>
          <Box marginTop="16px">
            <Input
              name="SuppliedEmail"
              label="Confirme seu e-mail de contato"
              placeholder="contato@empresa.com"
              helper="Vamos responder seu chamado pelo e-mail."
              onChange={(e) =>
                handleFormDataChange({ SuppliedEmail: e.target.value })
              }
              value={formData.SuppliedEmail}
            />
          </Box>
          <Flex
            justifyContent="space-between"
            borderTop="1px solid #EBEBEB"
            paddingVertical="16px"
          >
            <Button
              variation="secondary"
              className="theme-changeable"
              onClick={() => setStep("reason")}
            >
              voltar
            </Button>
            <Button onClick={handleSubmit} isLoading={isPending}>
              <SuccessOutline />
              criar chamado
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
}
